import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { Globe, LayoutDashboard, Map, Settings, Sparkles, BarChart2, PanelLeftClose, PanelLeftOpen, FileText, BookOpen, LogOut, Home, ShieldCheck, DollarSign, Languages, Activity, FlaskConical, Bot, Megaphone, Menu, X, TrendingUp, Mail, Inbox, Zap } from 'lucide-react';
import CountryDashboard from './CountryDashboard';
import CountriesPage from './CountriesPage';
import BlueprintConfigPage from './BlueprintConfigPage';
import SectionGeneratorPage from './SectionGeneratorPage';
import BlueprintViewPage from './BlueprintViewPage';
import ComparisonPage from './ComparisonPage';
import PlatformDocsPage from './PlatformDocsPage';
import UseCasesPage from './UseCasesPage';
import AdminConfigPage from './AdminConfigPage';
import PricingPage from './PricingPage';
import PaymentSuccessPage from './PaymentSuccessPage';
import FleetHealthPage from './FleetHealthPage';
import AdminABEventsPage from './AdminABEventsPage';
import AdminTrafficPage from './AdminTrafficPage';
import AdminEmailTestPage from './AdminEmailTestPage';
import ReportBuilderPage from './ReportBuilderPage';
import AdminJobsPage from './AdminJobsPage';
import AutomationPage from './AutomationPage';
import PRActivitiesPage from './PRActivitiesPage';
import { useLanguage } from '../contexts/LanguageContext';

const LANG_OPTIONS = [
  { code: 'en', label: 'EN', flag: '🇬🇧' },
  { code: 'fr', label: 'FR', flag: '🇫🇷' },
  { code: 'pt', label: 'PT', flag: '🇧🇷' },
];

function Dashboard({ user, onLogout }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t, lang, setLang } = useLanguage();
  const [showLangMenu, setShowLangMenu] = useState(false);

  // Close mobile drawer when route changes
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Close drawer when crossing into desktop & lock body scroll while open
  useEffect(() => {
    const onResize = () => { if (window.innerWidth >= 1024) setMobileOpen(false); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const isAdmin = user?.role === 'Admin';
  // Per-item icon colors — multicolor sidebar. Active state still flips to
  // brand blue via the active style block below, so these only show when the
  // item is at rest / hovered.
  const navItems = [
    { path: '/platform',                icon: LayoutDashboard, color: '#004ac6', label: t('nav_dashboard') },
    { path: '/platform/countries',      icon: Map,             color: '#059669', label: t('nav_countries') },
    { path: '/platform/report-builder',  icon: Inbox,           color: '#7c3aed', label: 'Report Builder' },
    { path: '/platform/blueprint-config', icon: Settings,      color: '#0891b2', label: t('nav_configuration') },
    // Generator & Compare are admin-only tools
    ...(isAdmin ? [
      { path: '/platform/generator',  icon: Sparkles,  color: '#7c3aed', label: t('nav_generator') },
      { path: '/platform/comparison', icon: BarChart2, color: '#ea580c', label: t('nav_compare') },
    ] : []),
    { path: '/platform/docs',       icon: FileText, color: '#2563eb', label: t('nav_documents') },
    { path: '/platform/use-cases',  icon: BookOpen, color: '#9333ea', label: t('nav_use_cases') },
    // Automation is available to all authenticated users
    { path: '/platform/automation', icon: Bot,      color: '#06b6d4', label: 'Automation' },
    ...(isAdmin ? [
      { path: '/platform/admin',     icon: ShieldCheck,  color: '#16a34a', label: t('nav_user_management') },
      { path: '/platform/fleet',     icon: Activity,     color: '#dc2626', label: 'Fleet Health' },
      { path: '/platform/ab-events', icon: FlaskConical, color: '#db2777', label: 'A/B Events' },
      { path: '/platform/traffic',   icon: TrendingUp,   color: '#10b981', label: 'Traffic' },
      { path: '/platform/email-test', icon: Mail,         color: '#004ac6', label: 'Email Test' },
      { path: '/platform/jobs',        icon: Zap,          color: '#dc2626', label: 'Job Control' },
      { path: '/platform/pricing',   icon: DollarSign,   color: '#ca8a04', label: t('nav_pricing_model') },
      { path: '/platform/pr',        icon: Megaphone,    color: '#e11d48', label: 'PR Activities' },
    ] : []),
  ];

  const isActive = (path) => {
    if (path === '/platform') return location.pathname === '/platform';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="flex min-h-screen bg-[#f8f9ff]" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
          data-testid="dashboard-mobile-backdrop"
        />
      )}
      {/* Collapsible Sidebar — drawer on mobile, sticky rail on desktop */}
      <aside
        data-testid="sidebar"
        className={`${collapsed ? 'lg:w-[68px]' : 'lg:w-64'} w-64 fixed lg:sticky inset-y-0 left-0 z-50 lg:z-auto h-screen bg-gradient-to-b from-white via-white to-[#f8f9ff] border-r border-[#c3c6d7]/30 flex flex-col shrink-0 top-0 overflow-hidden shadow-[1px_0_0_0_rgba(0,74,198,0.04)] transition-transform duration-300 ease-in-out ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
      >
        <style>{`
          @keyframes sidebarGlow { 0%, 100% { opacity: 0.5; } 50% { opacity: 0.85; } }
          .sidebar-active-glow { animation: sidebarGlow 3s ease-in-out infinite; }
          .sidebar-item-enter { transition: transform 0.18s cubic-bezier(0.22,1,0.36,1), background-color 0.18s; }
          .sidebar-item-enter:hover { transform: translateX(2px); }
          .sidebar-active-pill { position: relative; }
          .sidebar-active-pill::before { content: ''; position: absolute; left: -12px; top: 50%; transform: translateY(-50%); width: 3px; height: 60%; background: linear-gradient(180deg, #004ac6, #7c3aed); border-radius: 0 2px 2px 0; box-shadow: 0 0 8px rgba(0,74,198,0.4); }
        `}</style>
        {/* Subtle ambient glow */}
        <div className="absolute top-0 right-0 w-24 h-24 rounded-full bg-gradient-to-br from-[#004ac6]/5 to-[#7c3aed]/5 blur-2xl pointer-events-none sidebar-active-glow" />

        {/* Logo */}
        <div className={`${collapsed ? 'lg:px-3 lg:py-4 px-5 py-5' : 'px-5 py-5'} border-b border-[#c3c6d7]/20 flex items-center gap-3 cursor-pointer group relative z-10`} onClick={() => navigate('/')}>
          <div className="relative">
            <img src="/logo-small.png" alt="Sovereign IQ" className="w-9 h-9 shrink-0 rounded-lg object-contain transition-transform group-hover:scale-110 group-hover:rotate-3" />
            <div className="absolute -inset-1 bg-gradient-to-br from-[#004ac6]/20 to-[#7c3aed]/20 rounded-lg blur-md opacity-0 group-hover:opacity-100 transition-opacity -z-10" />
          </div>
          {(!collapsed || mobileOpen) && (
            <div className="overflow-hidden flex-1 lg:[&_.lg-hide-when-collapsed]:hidden">
              <h1 className="text-base font-bold tracking-tighter text-[#0b1c30] group-hover:bg-gradient-to-r group-hover:from-[#004ac6] group-hover:to-[#7c3aed] group-hover:bg-clip-text group-hover:text-transparent transition-all">Sovereign IQ</h1>
              <p className="text-[10px] text-[#434655] uppercase tracking-widest">Intelligence Platform</p>
            </div>
          )}
          {/* Mobile close button */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setMobileOpen(false); }}
            data-testid="dashboard-mobile-close"
            aria-label="Close menu"
            className="lg:hidden ml-auto p-2 -mr-1 rounded-lg hover:bg-[#eff4ff] active:scale-95 transition-all"
          >
            <X className="w-5 h-5 text-[#0b1c30]" />
          </button>
        </div>

        {/* Navigation — Home link first */}
        <nav className={`flex-1 ${collapsed ? 'p-2' : 'p-3'} space-y-0.5 overflow-y-auto overflow-x-visible relative z-10`}>
          <div onClick={() => navigate('/')} className={`sidebar-item-enter flex items-center ${collapsed ? 'justify-center' : 'gap-3'} ${collapsed ? 'p-2.5' : 'px-3 py-2.5'} rounded-lg text-sm cursor-pointer text-[#434655] hover:text-[#004ac6] hover:bg-gradient-to-r hover:from-[#eff4ff] hover:to-transparent`} title={collapsed ? t('nav_home') : undefined} data-testid="nav-home">
            <Home className="w-[18px] h-[18px] shrink-0" style={{ color: '#0ea5e9' }} />
            {!collapsed && <span className="truncate tracking-tight">{t('nav_home')}</span>}
          </div>
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link key={item.path} to={item.path}>
                <div
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  className={`sidebar-item-enter flex items-center ${collapsed ? 'justify-center' : 'gap-3'} ${collapsed ? 'p-2.5' : 'px-3 py-2.5'} rounded-lg text-sm cursor-pointer ${
                    active
                      ? 'sidebar-active-pill bg-gradient-to-r from-[#e5eeff] via-[#eff4ff] to-transparent text-[#004ac6] font-semibold shadow-sm'
                      : 'text-[#434655] hover:text-[#004ac6] hover:bg-gradient-to-r hover:from-[#eff4ff] hover:to-transparent'
                  }`}
                  title={collapsed ? item.label : undefined}
                >
                  <Icon
                    className={`w-[18px] h-[18px] shrink-0 transition-transform ${active ? 'scale-110' : ''}`}
                    style={active ? undefined : { color: item.color }}
                  />
                  {!collapsed && <span className="truncate tracking-tight">{item.label}</span>}
                  {!collapsed && active && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-[#004ac6] animate-pulse shrink-0" />}
                </div>
              </Link>
            );
          })}
        </nav>

        {/* Collapse Toggle */}
        <div className={`${collapsed ? 'px-2' : 'px-3'} py-2 border-t border-[#c3c6d7]/20`}>
          {/* Language Selector */}
          <div className="relative mb-1">
            <button
              onClick={() => setShowLangMenu(!showLangMenu)}
              data-testid="lang-selector-btn"
              className={`w-full flex items-center ${collapsed ? 'justify-center' : 'gap-2'} p-2 rounded-lg text-[#434655] hover:bg-[#eff4ff] hover:text-[#004ac6] transition-all text-sm`}
            >
              <Languages className="w-4 h-4 shrink-0" />
              {!collapsed && <span className="text-xs tracking-tight">{LANG_OPTIONS.find(l => l.code === lang)?.flag} {lang.toUpperCase()}</span>}
            </button>
            {showLangMenu && (
              <div className={`absolute ${collapsed ? 'left-full ml-1' : 'left-0 right-0'} bottom-full mb-1 bg-white border border-[#c3c6d7]/30 rounded-lg shadow-lg z-50 overflow-hidden`} data-testid="lang-dropdown">
                {LANG_OPTIONS.map(opt => (
                  <button key={opt.code} onClick={() => { setLang(opt.code); setShowLangMenu(false); }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-[#eff4ff] transition-all ${lang === opt.code ? 'bg-[#e5eeff] text-[#004ac6] font-semibold' : 'text-[#434655]'}`}
                    data-testid={`lang-option-${opt.code}`}>
                    <span>{opt.flag}</span>
                    <span>{opt.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={() => setCollapsed(!collapsed)}
            data-testid="sidebar-toggle"
            className="hidden lg:flex w-full items-center justify-center gap-2 p-2 rounded-lg text-[#434655] hover:bg-[#eff4ff] hover:text-[#004ac6] transition-all text-sm"
          >
            {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
            {!collapsed && <span className="text-xs tracking-tight">{t('nav_collapse')}</span>}
          </button>
        </div>

        {/* User */}
        <div className={`${collapsed ? 'px-2 py-2' : 'px-3 py-2'} border-t border-[#c3c6d7]/20 relative z-10`}>
          {!collapsed ? (
            <div className="flex items-center justify-between gap-2 p-2 rounded-lg bg-gradient-to-br from-[#f8f9ff] to-transparent">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#004ac6] to-[#7c3aed] text-white flex items-center justify-center text-xs font-black tracking-tighter shrink-0 shadow-sm">
                  {(user?.full_name || user?.email || 'U').trim().charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-[#0b1c30] truncate">{user?.full_name || 'User'}</p>
                  <p className="text-[10px] text-[#434655] truncate">{user?.email}</p>
                </div>
              </div>
              <button onClick={onLogout} data-testid="logout-btn" className="p-1.5 rounded-lg text-[#434655] hover:bg-red-50 hover:text-red-600 transition-all shrink-0" title="Logout">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button onClick={onLogout} data-testid="logout-btn" className="w-full flex justify-center p-2 rounded-lg text-[#434655] hover:bg-red-50 hover:text-red-600 transition-all" title="Logout">
              <LogOut className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Branding */}
        <div className={`${collapsed ? 'px-2 py-3' : 'px-3 py-3'} border-t border-[#c3c6d7]/20`}>
          {!collapsed ? (
            <div className="px-2">
              <img src="/logo-big.png" alt="Sovereign IQ Intelligence Platform" className="w-full h-auto object-contain" />
            </div>
          ) : (
            <div className="flex justify-center">
              <img src="/logo-small.png" alt="Sovereign IQ" className="w-8 h-8 object-contain" />
            </div>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0 overflow-auto">
        {/* Mobile header — visible only when sidebar is hidden */}
        <header
          className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-3 bg-white border-b border-[#c3c6d7]/30 shadow-sm"
          data-testid="dashboard-mobile-header"
        >
          <div onClick={() => navigate('/')} className="flex items-center gap-2 cursor-pointer">
            <img src="/logo-small.png" alt="Sovereign IQ" className="w-7 h-7 rounded-md object-contain" />
            <div>
              <div className="text-[13px] font-extrabold tracking-tight text-[#0b1c30] leading-tight">Sovereign IQ</div>
              <div className="text-[9px] uppercase tracking-widest text-[#737686] leading-tight">Intelligence Platform</div>
            </div>
          </div>
          <button
            onClick={() => setMobileOpen(true)}
            data-testid="dashboard-mobile-hamburger"
            aria-label="Open menu"
            className="p-2 -mr-2 rounded-lg hover:bg-[#eff4ff] active:scale-95 transition-all"
          >
            <Menu className="w-5 h-5 text-[#0b1c30]" />
          </button>
        </header>
        <div key={location.pathname} className="page-transition">
          <Routes>
            <Route path="/" element={<CountryDashboard user={user} />} />
            <Route path="/countries" element={<CountriesPage />} />
            <Route path="/report-builder" element={<ReportBuilderPage />} />
            <Route path="/countries/:countryId/blueprint" element={<BlueprintViewPage />} />
            <Route path="/blueprint-config" element={<BlueprintConfigPage />} />
            {/* Generator & Comparison are admin-only tools */}
            {isAdmin && <Route path="/generator" element={<SectionGeneratorPage />} />}
            {isAdmin && <Route path="/comparison" element={<ComparisonPage />} />}
            <Route path="/docs" element={<PlatformDocsPage />} />
            <Route path="/use-cases" element={<UseCasesPage />} />
            {/* Automation is available to all authenticated users */}
            <Route path="/automation" element={<AutomationPage />} />
            {isAdmin && <Route path="/admin" element={<AdminConfigPage />} />}
            {isAdmin && <Route path="/fleet" element={<FleetHealthPage />} />}
            {isAdmin && <Route path="/ab-events" element={<AdminABEventsPage />} />}
            {isAdmin && <Route path="/traffic" element={<AdminTrafficPage />} />}
            {isAdmin && <Route path="/email-test" element={<AdminEmailTestPage />} />}
            {isAdmin && <Route path="/jobs" element={<AdminJobsPage />} />}
            {isAdmin && <Route path="/pricing" element={<PricingPage />} />}
            {isAdmin && <Route path="/pr" element={<PRActivitiesPage />} />}
            <Route path="/payment-success" element={<PaymentSuccessPage />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

export default Dashboard;
